import React, { useState, useEffect, useRef } from 'react';

/** Merge API options with placeholder rows for selected ids missing from the list (inactive users, pagination gaps). */
export function buildCaregiverOptionsWithStubs(options, selectedIds, extraKnownObjects = []) {
  const list = Array.isArray(options) ? options : [];
  const knownMap = new Map();
  for (const o of list) {
    if (o && o.id != null) knownMap.set(String(o.id), o);
  }
  for (const o of extraKnownObjects || []) {
    if (o && o.id != null && !knownMap.has(String(o.id))) {
      knownMap.set(String(o.id), o);
    }
  }

  const out = Array.from(knownMap.values());
  const idSet = new Set(out.map((o) => String(o.id)));

  for (const sid of selectedIds || []) {
    const s = String(sid).trim();
    if (!s || idSet.has(s)) continue;
    const n = Number(s);
    out.push({
      id: Number.isFinite(n) && !Number.isNaN(n) ? n : s,
      first_name: '',
      last_name: ''
    });
    idSet.add(s);
  }
  return out;
}

export function isStaffActive(row) {
  if (!row) return true;
  const s = row.status ?? row.user?.status;
  if (s === null || s === undefined) return true;
  if (typeof s === 'boolean') return s;
  const str = String(s).trim().toLowerCase();
  if (['active', '1', '2', 'true', 'enabled'].includes(str)) return true;
  if (['inactive', '0', '3', '4', 'false', 'disabled', 'locked'].includes(str)) return false;
  return true;
}

export function personPickerLabel(row) {
  if (!row) return '';
  let label = '';
  const a = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
  if (a) label = a;
  else if (row.full_name) label = row.full_name;
  else if (row.name) label = row.name;

  if (label) {
    if (!isStaffActive(row)) {
      return `${label} (Inactive)`;
    }
    return label;
  }
  return row.id != null ? `ID ${row.id}` : '';
}

/** Split API ids that were stored as one comma-separated string (e.g. `['123,456,789']`). */
export function expandCaregiverIdsFromApi(raw, singleFallback) {
  const parts = [];
  const pushToken = (t) => {
    const s = String(t).trim();
    if (!s) return;
    if (s.startsWith('[')) {
      try {
        const inner = JSON.parse(s);
        if (Array.isArray(inner)) {
          inner.forEach((x) => pushToken(x));
          return;
        }
      } catch {
        /* fall through */
      }
    }
    if (s.includes(',')) s.split(',').forEach((p) => pushToken(p.trim()));
    else parts.push(s.replace(/^\[|\]$/g, ''));
  };
  if (Array.isArray(raw) && raw.length > 0) {
    raw.forEach((x) => pushToken(x));
    return [...new Set(parts.filter(Boolean))];
  }
  const one = singleFallback;
  if (one != null && String(one).trim() !== '') {
    pushToken(one);
    return [...new Set(parts.filter(Boolean))];
  }
  return [];
}

export function resolvePracticeCaregiverIdsFromPatient(patient) {
  const fromJunction = expandCaregiverIdsFromApi(
    patient?.practice_caregiver_ids || patient?.practiceCaregiverIds,
    null
  );
  const fromColumn = expandCaregiverIdsFromApi(null, patient?.caregiver_id);
  const seen = new Set();
  const out = [];
  for (const id of [...fromJunction, ...fromColumn]) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(String(id));
  }
  if (out.length > 0) return out;
  const one = patient?.practice_caregiver_id ?? patient?.practiceCaregiver?.id ?? patient?.practiceCaregiverId;
  return one != null && String(one).trim() !== '' ? [String(one)] : [];
}

export function toNumericCaregiverIds(ids) {
  return (ids || [])
    .map((id) => parseInt(String(id), 10))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export function PatientAscStyleSinglePicker({
  label,
  name,
  required,
  value,
  options,
  error,
  emptyMessage,
  placeholder,
  onPick,
  id
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = options.find((o) => String(o.id) === String(value));
  const display = selected ? personPickerLabel(selected) : '';

  return (
    <div className="add-patient-form-group add-patient-asc-pick-root" ref={rootRef} id={id}>
      <label>
        {label}
        {required ? (
          <>
            {' '}
            <span className="add-patient-required">*</span>
          </>
        ) : null}
      </label>
      <div className="add-patient-asc-pick-shell">
        <div
          className={`add-patient-asc-pick-trigger ${error ? 'is-invalid' : ''} ${open ? 'is-open' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((o) => !o);
            }
          }}
        >
          {!value || !selected ? (
            <span className="add-patient-asc-pick-placeholder">{placeholder}</span>
          ) : (
            <span className="add-patient-asc-pick-selected-text">{display}</span>
          )}
        </div>
        {open ? (
          <ul className="add-patient-asc-pick-list" role="listbox">
            {options.length === 0 ? (
              <li className="add-patient-asc-pick-empty">{emptyMessage}</li>
            ) : (
              options.map((opt) => (
                <li
                  key={opt.id}
                  role="option"
                  aria-selected={String(opt.id) === String(value)}
                  className={`add-patient-asc-pick-option ${String(opt.id) === String(value) ? 'is-selected' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onPick(name, String(opt.id));
                    setOpen(false);
                  }}
                >
                  {personPickerLabel(opt)}
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
      {error ? <div className="add-patient-error-message">{error}</div> : null}
    </div>
  );
}

export function PatientAscStyleMultiPicker({
  label,
  required,
  selectedIds,
  options,
  onAddId,
  onRemoveId,
  error,
  emptyMessage,
  placeholder,
  id
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const sel = (selectedIds || []).map((x) => String(x));
  const available = options.filter((o) => !sel.includes(String(o.id)));

  return (
    <div className="add-patient-form-group add-patient-asc-pick-root" ref={rootRef} id={id}>
      <label>
        {label}
        {required ? (
          <>
            {' '}
            <span className="add-patient-required">*</span>
          </>
        ) : null}
      </label>
      <div className="add-patient-asc-pick-shell">
        <div
          className={`add-patient-asc-pick-trigger add-patient-asc-pick-trigger--multi ${error ? 'is-invalid' : ''} ${open ? 'is-open' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((o) => !o);
            }
          }}
        >
          {sel.length === 0 ? (
            <span className="add-patient-asc-pick-placeholder">{placeholder}</span>
          ) : (
            sel.map((idStr) => {
              const row = options.find((o) => String(o.id) === String(idStr));
              const chipText = row ? personPickerLabel(row) : `ID ${idStr}`;
              return (
                <span key={idStr} className="add-patient-asc-pick-chip">
                  <span className="add-patient-asc-pick-chip-text">{chipText}</span>
                  <button
                    type="button"
                    className="add-patient-asc-pick-chip-remove"
                    aria-label={`Remove ${chipText}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveId(idStr);
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })
          )}
        </div>
        {open ? (
          <ul className="add-patient-asc-pick-list" role="listbox">
            {options.length === 0 ? (
              <li className="add-patient-asc-pick-empty">{emptyMessage}</li>
            ) : available.length === 0 ? (
              <li className="add-patient-asc-pick-empty">All selected</li>
            ) : (
              available.map((opt) => (
                <li
                  key={opt.id}
                  role="option"
                  aria-selected={false}
                  className="add-patient-asc-pick-option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onAddId(String(opt.id));
                    setOpen(false);
                  }}
                >
                  {personPickerLabel(opt)}
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
      {error ? <div className="add-patient-error-message">{error}</div> : null}
    </div>
  );
}
